/**
 * Metrics Service
 * Pure functions for calculating acceptance criteria progress, CI check summaries,
 * and attention status badge tones.
 */

import { AcceptanceCriterion, CheckRun, AttentionStatus, Tone } from '../domain/types';

export interface AcceptanceProgress {
  done: number;
  total: number;
  pct: number;
}

export interface CheckSummary {
  passed: number;
  warnings: number;
  fails: number;
  total: number;
  allPassed: boolean;
}

type CriteriaItem = [string, string] | AcceptanceCriterion;
type CheckItem = [string, string, string, string, string[]] | CheckRun;

const DONE_LABELS = new Set(['done', 'hecho']);
const PASSED_STATUSES = new Set(['ok', 'passed', 'success']);
const WARN_STATUSES = new Set(['warn', 'warning']);
const FAIL_STATUSES = new Set(['fail', 'failed', 'error']);

const BADGE_TONES: Record<string, Tone> = {
  you: 'bad',
  ready: 'ok',
  blocked: 'warn',
  them: 'flat',
};

function overrideForCriterion(
  item: CriteriaItem,
  index: number,
  overrides?: Record<string | number, boolean | string>,
): boolean | string | undefined {
  if (!overrides) return undefined;
  if (Array.isArray(item)) return overrides[item[0]] ?? overrides[index];
  if (item.id) return overrides[item.id] ?? overrides[index];
  return overrides[index];
}

function isDoneLabel(value: string | undefined): boolean {
  return DONE_LABELS.has((value || '').toLowerCase());
}

function isDoneFromOverride(activeOverride: boolean | string): boolean {
  if (typeof activeOverride === 'boolean') return activeOverride;
  return isDoneLabel(activeOverride);
}

function tupleIsDone(item: [string, string]): boolean {
  return isDoneLabel(item[0]) || isDoneLabel(item[1]);
}

function objectIsDone(item: AcceptanceCriterion): boolean {
  return isDoneLabel(item.status) || isDoneLabel(item.state);
}

function isDoneFromCriterion(item: CriteriaItem): boolean {
  if (Array.isArray(item)) return tupleIsDone(item);
  return objectIsDone(item);
}

function isCriterionDone(
  item: CriteriaItem,
  index: number,
  overrides?: Record<string | number, boolean | string>,
): boolean {
  const activeOverride = overrideForCriterion(item, index, overrides);
  if (activeOverride !== undefined) return isDoneFromOverride(activeOverride);
  return isDoneFromCriterion(item);
}

function hasCriteria(
  criteria?: Array<[string, string]> | AcceptanceCriterion[],
): criteria is Array<[string, string]> | AcceptanceCriterion[] {
  return Boolean(criteria && criteria.length > 0);
}

/**
 * Calculates acceptance criteria completion progress.
 */
export function calculateAcceptanceProgress(
  criteria?: Array<[string, string]> | AcceptanceCriterion[],
  overrides?: Record<string | number, boolean | string>
): AcceptanceProgress {
  if (!hasCriteria(criteria)) {
    return { done: 0, total: 0, pct: 0 };
  }

  let done = 0;
  for (let i = 0; i < criteria.length; i++) {
    if (isCriterionDone(criteria[i], i, overrides)) done++;
  }

  return { done, total: criteria.length, pct: Math.round((done / criteria.length) * 100) };
}

function checkStatus(check: CheckItem): string {
  if (Array.isArray(check)) return (check[1] || '').toLowerCase();
  return ((check.conclusion || check.status) || '').toLowerCase();
}

function tallyCheckStatus(
  status: string,
  counts: { passed: number; warnings: number; fails: number },
): void {
  if (PASSED_STATUSES.has(status)) counts.passed++;
  else if (WARN_STATUSES.has(status)) counts.warnings++;
  else if (FAIL_STATUSES.has(status)) counts.fails++;
}

/**
 * Summarizes CI/CD checks and test runs.
 */
export function getCheckSummary(
  checks?: Array<[string, string, string, string, string[]]> | CheckRun[]
): CheckSummary {
  if (!checks || checks.length === 0) {
    return { passed: 0, warnings: 0, fails: 0, total: 0, allPassed: false };
  }

  const counts = { passed: 0, warnings: 0, fails: 0 };
  for (const check of checks) {
    tallyCheckStatus(checkStatus(check), counts);
  }

  const total = checks.length;
  const allPassed = counts.fails === 0 && counts.warnings === 0 && counts.passed === total;
  return { ...counts, total, allPassed };
}

/**
 * Maps attention item status to appropriate UI badge tone.
 */
export function getAttentionBadgeTone(status: AttentionStatus | string): Tone {
  return BADGE_TONES[status] ?? 'flat';
}

export const MetricsService = {
  calculateAcceptanceProgress,
  getCheckSummary,
  getAttentionBadgeTone,
};
